import { useCallback, useEffect, useState } from "react";
import { PeakEvent } from "../types/event";
import { getAllEvents } from "../services/eventService";

// Per-session cache of an athlete's events (events per athlete are few), so
// the diary, Performances and Dashboard do not each refetch the subcollection.
const cache = new Map<string, PeakEvent[]>();

/** Drop the cached events after a create / edit / delete. */
export function invalidateUserEvents(userId?: string) {
  if (userId) cache.delete(userId);
  else cache.clear();
}

/** The athlete's events (all of them), cached for the session. */
export function useUserEvents(userId: string | undefined) {
  const [events, setEvents] = useState<PeakEvent[]>(
    userId ? (cache.get(userId) ?? []) : [],
  );
  const [loading, setLoading] = useState(!!userId && !cache.has(userId));

  const load = useCallback(
    async (force = false) => {
      if (!userId) {
        setEvents([]);
        setLoading(false);
        return;
      }
      if (!force && cache.has(userId)) {
        setEvents(cache.get(userId) ?? []);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const list = await getAllEvents(userId);
        cache.set(userId, list);
        setEvents(list);
      } catch (error) {
        console.error("Error loading events:", error);
      } finally {
        setLoading(false);
      }
    },
    [userId],
  );

  useEffect(() => {
    load();
  }, [load]);

  const reload = useCallback(() => load(true), [load]);
  return { events, loading, reload };
}
